import { useSwitchTenant } from '@/api/mutations/useAuth';
import { signedInPath } from '@/lib/auth/signedInDestination';
import { redirectBrowser } from '@/lib/browser-redirect';
import { withWorkspaceSwitchSplash } from '@/stores/auth-store';

/**
 * Switch the session into another workspace, shared by the select-workspace
 * screen and the user-menu switcher.
 *
 * A full page load, not an SPA transition: every cached byte is
 * tenant-scoped, and the in-app sequence (clear the query cache under the
 * mounted tree, invalidate, navigate) raced itself — cancelled loaders
 * flashed the root ErrorFallback before the destination rendered (the same
 * lesson useLogout documents). The unload wipes the in-memory cache and the
 * new document boots from the token setAuth persisted, so there is nothing
 * to reconcile. The splash covers the app from click to unload; it only
 * comes down again if the switch itself fails.
 */
export function useSwitchWorkspace() {
  const switchTenant = useSwitchTenant();

  const switchWorkspace = async (tenantId: string) => {
    try {
      const session = await withWorkspaceSwitchSplash(() => switchTenant.mutateAsync({ tenantId }));
      redirectBrowser(signedInPath(session));
    } catch {
      // The global interceptor already surfaced the error.
    }
  };

  return { switchWorkspace, isSwitching: switchTenant.isPending };
}
